import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import { AutoAwesome, ExpandLess, ExpandMore } from '@mui/icons-material';
import { AISummaryText } from './AISummaryText';

/** One line of summary text (body2 at line height 1.6). */
const LINE_PX = 22.4;
const EASE = '300ms cubic-bezier(0.4, 0, 0.2, 1)';
const FADE = 'linear-gradient(to bottom, #000 35%, transparent 98%)';

const tint = (theme: Theme, hover: boolean) =>
  theme.palette.mode === 'dark'
    ? `rgba(98, 191, 123, ${hover ? 0.11 : 0.06})`
    : `rgba(46, 125, 50, ${hover ? 0.08 : 0.04})`;

/** An AI summary that never moves the page: it holds the same height while loading and once the answer arrives (the
 * text fading out toward the bottom), and grows only when the reader clicks (or taps) anywhere in the box. */
export const AISummaryPanel: React.FC<{
  label: React.ReactNode;
  isLoading: boolean;
  error?: Error | null;
  text?: string;
  onRetry?: () => void;
  /** Lines shown, fading out, before the box is opened. */
  lines?: number;
  sx?: SxProps<Theme>;
}> = ({ label, isLoading, error, text, onRetry, lines = 4, sx }) => {
  const collapsed = lines * LINE_PX;
  const innerRef = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const ready = !isLoading && !error && !!text;

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!ready || !el) return;
    const measure = () => setFull(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready, text]);

  const overflows = ready && full > collapsed + 2;
  const open = expanded && overflows;
  const toggle = () => {
    // don't fold the text away from under someone selecting it
    if (open && window.getSelection()?.toString()) return;
    setExpanded((v) => !v);
  };

  return (
    <Box
      {...(overflows && {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': open,
        onClick: toggle,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        },
      })}
      sx={[
        {
          '@keyframes aiFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          position: 'relative',
          p: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          backgroundColor: (theme) => tint(theme, false),
          transition: 'border-color 150ms ease, background-color 150ms ease',
          ...(overflows && {
            cursor: 'pointer',
            outline: 'none',
            '@media (hover: hover)': {
              '&:hover': { borderColor: 'primary.main', backgroundColor: (theme: Theme) => tint(theme, true) },
              '&:hover .ai-chevron': { opacity: 1, transform: open ? 'translateY(-2px)' : 'translateY(2px)' },
            },
            '&:focus-visible': { borderColor: 'primary.main', backgroundColor: (theme: Theme) => tint(theme, true) },
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, minHeight: 20 }}
      >
        <AutoAwesome sx={{ fontSize: 14, color: 'primary.main' }} /> {label}
      </Typography>
      <Box
        sx={{
          height: ready ? (open ? full : collapsed) : collapsed,
          minHeight: collapsed,
          overflow: 'hidden',
          transition: `height ${EASE}`,
          // the rest of the text fades out toward the bottom until the box is opened
          ...(overflows && !open && { maskImage: FADE, WebkitMaskImage: FADE }),
        }}
      >
        {isLoading ? (
          <Box role="status" aria-label="Writing the summary">
            {Array.from({ length: lines }, (_, i) => (
              <Skeleton key={i} variant="text" height={LINE_PX} width={i === lines - 1 ? '70%' : undefined} />
            ))}
          </Box>
        ) : error ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {error instanceof TypeError ? "Couldn't load the summary (the connection dropped)." : error.message}{' '}
            {onRetry && (
              <ButtonBase
                onClick={onRetry}
                sx={{ color: 'primary.main', fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline' }}
              >
                Try again
              </ButtonBase>
            )}
          </Typography>
        ) : (
          <Box ref={innerRef} sx={{ animation: 'aiFadeIn 400ms ease-out' }}>
            <AISummaryText text={text ?? ''} />
          </Box>
        )}
      </Box>
      {overflows && (
        <Box
          className="ai-chevron"
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: 0,
            ml: '-10px',
            display: 'flex',
            color: 'primary.main',
            opacity: 0.75,
            transition: 'opacity 150ms ease, transform 150ms ease',
            animation: 'aiFadeIn 400ms ease-out',
          }}
        >
          {open ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
        </Box>
      )}
    </Box>
  );
};
